import { Component, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { CommonModule } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize } from 'rxjs';
import { LoaderComponent } from '../../loader/loader.component';
import { LoaderService } from '../../../services/loader/loader.service';
import { HolidaysService } from '../../../services/holidays/holidays.service';
import { StorageService } from '../../../services/storage/storage.service';
import { RolesService } from '../../../services/roles/roles.service';

@Component({
  selector: 'app-priority-period-form',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatDatepickerModule,
    FormsModule,
    RouterLink,
    LoaderComponent,
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './priority-period-form.component.html',
  styleUrls: ['./priority-period-form.component.css', '../../../styles/layout.css'],
})
export class PriorityPeriodFormComponent {
  // Mode management
  isEditMode: boolean = false;
  periodId: number | null = null;

  // Common properties
  myroles: any = {};
  priorityPeriodForm: FormGroup;
  period: any = {};
  exercises: any[] = [];
  isSubmitting = false;
  attemptedSubmit = false;
  returnLink: string = '/home/vacations/priority-periods';

  private _snackBar = inject(MatSnackBar);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private holidaysService: HolidaysService,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private loaderService: LoaderService,
    private storageService: StorageService,
    private rolesService: RolesService
  ) {
    this.priorityPeriodForm = this.fb.group({
      exercise: [null, [Validators.required]],
      min_years_worked: [null, [Validators.required, Validators.min(0)]],
      start_date: [null, [Validators.required]],
      end_date: [null, [Validators.required]],
    }, { validators: this.dateRangeValidator });
  }

  // Custom validator to ensure end date >= start date
  private dateRangeValidator(control: any): { [key: string]: boolean } | null {
    const startDate = control.get('start_date')?.value;
    const endDate = control.get('end_date')?.value;

    if (!startDate || !endDate) {
      return null;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (end < start) {
      return { dateRangeInvalid: true };
    }

    return null;
  }

  get isLoading() {
    return this.loaderService.getLoader();
  }

  ngOnInit() {
    this.route.params.subscribe((params) => {
      if (params['id']) {
        this.isEditMode = true;
        this.periodId = +params['id'];
        this.loadExercises();
        this.loadPriorityPeriodData();
      } else {
        this.isEditMode = false;
        this.periodId = null;
        this.loadExercises();
      }
    });
    this.loadRoles();
  }

  loadRoles() {
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'Vacaciones',
      this.storageService
    );

    if (permissions) {
      this.myroles = permissions;

      const permissionMessage = this.isEditMode
        ? 'No tienes permisos para editar un período de prioridad'
        : 'No tienes permisos para añadir un período de prioridad';

      this.rolesService.checkWritePermission(
        'Vacaciones',
        this.storageService,
        this._snackBar,
        this.router,
        this.returnLink,
        permissionMessage
      );
    }
  }

  private loadPriorityPeriodData(): void {
    if (!this.periodId) return;

    this.loaderService.showLoader();
    this.holidaysService
      .getVacationPriorityPeriodById(this.periodId)
      .pipe(
        finalize(() => {
          this.loaderService.hideLoader();
        })
      )
      .subscribe({
        next: (data: any) => {
          this.period = data;
          this.priorityPeriodForm.patchValue({
            exercise: data.exercise,
            min_years_worked: data.min_years_worked,
            start_date: data.start_date,
            end_date: data.end_date,
          });
        },
        error: (error: any) => {
          console.error('Error loading priority period:', error);
          this.snackBar.open(
            'Error al cargar los datos del período de prioridad',
            'Cerrar',
            { duration: 3000 }
          );
          this.router.navigate([this.returnLink]);
        },
      });
  }

  private loadExercises(): void {
    this.loaderService.showLoader();
    this.holidaysService
      .getExercises(false)
      .pipe(
        finalize(() => {
          this.loaderService.hideLoader();
        })
      )
      .subscribe({
        next: (data) => {
          this.exercises = data.filter((e) => !e.is_deleted);
        },
        error: (error) => {
          console.error('Error loading exercises:', error);
          this.snackBar.open(
            'Error al cargar los ejercicios',
            'Cerrar',
            { duration: 3000 }
          );
        },
      });
  }

  saveChanges(): void {
    this.loaderService.showLoader();
    if (this.isSubmitting) return;
    this.attemptedSubmit = true;

    if (!this.priorityPeriodForm.valid) {
      this.loaderService.hideLoader();
      // Mark all controls as touched to trigger validation visuals
      Object.keys(this.priorityPeriodForm.controls).forEach((key) => {
        const control = this.priorityPeriodForm.get(key);
        control?.markAsTouched();
      });

      let errorMessage = 'Por favor completa todos los campos requeridos';
      if (this.priorityPeriodForm.get('min_years_worked')?.hasError('min')) {
        errorMessage = 'Los años mínimos no pueden ser negativos';
      } else if (this.priorityPeriodForm.hasError('dateRangeInvalid')) {
        errorMessage = 'La fecha de fin debe ser mayor o igual a la fecha de inicio';
      }

      this.snackBar.open(errorMessage, 'Cerrar', { duration: 5000 });
      return;
    }

    this.isSubmitting = true;

    if (this.isEditMode) {
      this.updatePriorityPeriod();
    } else {
      this.createPriorityPeriod();
    }
  }

  private createPriorityPeriod(): void {
    const formValue = this.priorityPeriodForm.value;

    const body = {
      exercise: formValue.exercise,
      min_years_worked: formValue.min_years_worked,
      start_date: this.formatDate(formValue.start_date),
      end_date: this.formatDate(formValue.end_date),
    };

    this.holidaysService
      .postVacationPriorityPeriod(body)
      .pipe(
        finalize(() => {
          this.loaderService.hideLoader();
          this.isSubmitting = false;
        })
      )
      .subscribe({
        next: (response) => {
          this.snackBar.open('Período de prioridad creado correctamente', 'Cerrar', {
            duration: 3000,
          });
          this.router.navigate([this.returnLink]);
        },
        error: (error) => {
          console.error('Error creating priority period:', error);
          this.snackBar.open('Error al crear el período de prioridad', 'Cerrar', {
            duration: 3000,
          });
        },
      });
  }

  private updatePriorityPeriod(): void {
    if (!this.periodId) return;

    const formValue = this.priorityPeriodForm.value;

    const body = {
      exercise: formValue.exercise,
      min_years_worked: formValue.min_years_worked,
      start_date: this.formatDate(formValue.start_date),
      end_date: this.formatDate(formValue.end_date),
    };

    this.holidaysService
      .updateVacationPriorityPeriod(this.periodId, body)
      .pipe(
        finalize(() => {
          this.loaderService.hideLoader();
          this.isSubmitting = false;
        })
      )
      .subscribe({
        next: (response: any) => {
          this.snackBar.open('Período de prioridad actualizado correctamente', 'Cerrar', {
            duration: 3000,
          });
          this.router.navigate([this.returnLink]);
        },
        error: (error: any) => {
          console.error('Error updating priority period:', error);
          this.snackBar.open('Error al actualizar el período de prioridad', 'Cerrar', {
            duration: 3000,
          });
        },
      });
  }

  private formatDate(date: any): string | null {
    if (!date) return null;
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  cancel(): void {
    this.router.navigate([this.returnLink]);
  }
}
